import { BuildingDTO } from "./building";
import { Contact, ContactCompany } from "./contact";
import { UnitDTO } from "./unit";
import { TodoItem } from "./todo-item";

export type AvailableRenovationLabels = {
    id: any;
    name: AvailableRenovationLabel;
  };

export enum AvailableRenovationLabel {
    ROOF = 'Dak',
    WINDOWS = 'Ramen',
    ELECTRONIC_LOCK = 'Electrisch slot',
    ELECTRICITY = 'Electriciteit',
    PIPING = 'Leidingen',
    HEATING_DEVICES = 'Verwarmingstoestellen',
    SUNSHADE = 'Zonnewering',
    WATER_WELL = 'Waterput',
    TERRACE = 'Terras',
    FLOOR_INSULATION = 'Isolatie vloeren',
    FLOOR_COVERING = 'Vloerbekleding',
    KITCHEN = 'Keuken',
    BATHROOM = 'Badkamer',
    FACADE_INSULATION = 'Gevelisolatie',
    NEW_FACADE_BRICK = 'Nieuwe gevelsteen',
    CONDENSATION_BOILER = 'Condensatieketel',
    SOLAR_PANELS = 'Zonnepanelen',
    HEAT_PUMP = 'Warmtepomp',
    PLASTERING = 'Bezetting',
    SEWERAGE_PVC = 'Riolering PVC',
    ELEVATOR = 'Lift',
    SEPTIC_TANK = 'Septische put',
    STABILITY = 'Stabiliteit',
    ELECTRIC_CAR_CHARGING_POINT = 'Electrisch laadpunt wagen',
    GREEN_WALL = 'Groengevel',
    GREEN_ROOF = 'Groendak',
    SOLAR_WATER_HEATER = 'Zonneboiler',
    SECURITY_GATE = 'Veiligheidspoort',
    ROLLER_SHUTTERS = 'Rolluiken',
    AIR_CONDITIONING = 'Airco',
    VENTILAION = 'Ventilatie',
    PIPES_HEATING = 'Leidingen - verwarming',
    COMPLETELY_RENOVATED_INTERIOR = 'Binnen reeds volledig gerenoveerd',
    FULLY_COMPLIANT = 'Volledig in orde'
  }

export enum ProjectStatus {
    DESIGN = "Ontwerp fase",
    PLANNED = "Gepland",
    IN_PROGRESS = "In uitvoering",
    COMPLETED = "Afgerond",
    PAID = "Volledig gefactureerd"
}

export enum PhaseType {
    PREPARATION = "Voorbereiding",
    DEMOLITION = "Afbraak",
    STRUCTURAL = "Ruwbouw",
    TECHNICAL = "Technisch",
    FINISHING = "Afwerking",
    INSPECTION = "Keuring",
    OTHER = "Andere"
}

export enum PhaseStatus {
    NOT_STARTED = "Niet gestart",
    REQUESTING_OFFERS = "Offertes aanvragen",
    REVIEWING_OFFERS = "Offertes beoordelen",
    SUPPLIER_SELECTED = "Leverancier gekozen",
    IN_PROGRESS = "In uitvoering",
    COMPLETED = "Afgerond",
    ON_HOLD = "On hold"
}

export enum ProjectDateType {
    EXPECTED_START = "Verwachte startdatum",
    EXPECTED_END = "Verwachte einddatum",
    ACTUAL_START = "Effectieve startdatum",
    ACTUAL_END = "Effectieve einddatum",
    CUSTOM = "CUSTOM"
}

export enum DurationType {
    DAYS = "dagen",
    WEEKS = "weken",
    MONTHS = "maanden",
    YEARS = "jaren"
}

export enum DocumentType {
    OFFER = "Offerte",
    INVOICE = "Factuur",
    ORDER = "Bestelbon",
    CONTRACT = "Contract",
    OTHER = "Overig"
}

export enum DocumentApprovalStatus {
    DRAFT = "Concept",
    RECEIVED = "Ontvangen",
    APPROVED = "Goedgekeurd",
    REJECTED = "Afgewezen",
}

export enum DocumentSignStatus {
    NOT_SIGNED = "Niet ondertekend",
    SIGNED_BY_SUPPLIER = "Ondertekend door tegenpartij",
    SIGNED_INTERNAL = "Ondertekend intern",
    SIGNED_ALL_PARTIES = "Ondertekend door alle partijen",
}

export enum MeasureType {
    M2 = 'm2',
    M3 = 'm3',
    UNIT = 'unit',
    PIECE = 'stuk'
}

export enum FinancialDocumentType {
    OFFER = "Offerte",
    BUDGET = "Budget",
    ORDER = "Bestelbon",
    INVOICE = "Factuur"
}

export interface DocumentLine {
    id?: number;
    description: string;
    analyticDistributions: AnalyticDistribution[];
    totalExclVat: number;
    vatPercentage: number;
    document: RenovationDocument;
    totalInclVat: number;
    measure?: MeasureType;
    quantity?: number;
    part: ProjectPart;
    pricePerMeasure?: number;
    vatAmount?: number;
    /**
     * Herkomst/betrouwbaarheid van de btw-velden: "odoo_tax" | "none" |
     * "import_failed". Zie shared/utils/invoiceVat. NULL voor regels van vóór de
     * btw-uitsplitsing en voor handmatige uploads.
     */
    vatSource?: string | null;
    remarks?: string;
    reference?: string;
    position: number;
}

export interface DocumentUnitAssignment {
    id: number;
    document: RenovationDocument;
    unit: UnitDTO;
    amount: number;
    description?: string;
}

export interface RenovationDocument {
    id: number;
    type: DocumentType;
    status: DocumentApprovalStatus;
    /**
     * Ondertekenstatus, los van de goedkeuringsstatus: een bestelbon kan
     * goedgekeurd zijn en nog niet getekend, of net omgekeerd. Optioneel, want
     * documenten van vóór deze kolom bewaard werd, hebben ze niet meegekregen.
     */
    signedStatus?: DocumentSignStatus;
    amount: number;
    documentDate: Date;
    remarks?: string;
    building: BuildingDTO;
    projects: RenovationProject[];
    lines: DocumentLine[];
    contactPerson?: Contact;
    supplier: ContactCompany;
    sharePointFileUrl?: string;
    sharePointFileId?: string;
    reference?: string;
    createdAt: Date;
    processedAt?: Date;
    label?: RenovationLabel;
    relatedDocument?: RenovationDocument;
    relatedDocuments: RenovationDocument[];
    documentNumber?: string;
    vatAmount: number;
    totalInclVat: number;
    validUntil?: Date;
    /**
     * Leverancierscreditnota (Odoo `move_type = 'in_refund'`, documentnummer
     * `RLF/…`). Een creditnota **verlaagt** de kost, dus elke optelling trekt ze
     * af. Het teken van de documentregels zegt dat niet — dat is padafhankelijk
     * (de importer bewaart negatief, de manuele upload niet) — dus is dit veld
     * de bron van waarheid. Zie `shared/utils/documentLineAmount.ts`.
     */
    isCreditNote?: boolean;
    /**
     * Odoo reconciliation status (set by the backend on imported documents):
     * true = still present in Odoo, false = the Odoo source is gone/changed,
     * undefined = not imported from Odoo (manually uploaded) → N/A.
     */
    existsInOdoo?: boolean;
    /**
     * Whether the YallyHub amount still equals the invoice amount in Odoo:
     * true = matches, false = differs, undefined = N/A (not imported, or gone
     * from Odoo so it can't be compared).
     */
    amountMatchesOdoo?: boolean;
    /**
     * Odoo payment status of the source invoice (account.move.payment_state),
     * refreshed daily: 'paid' | 'not_paid' | 'partial' | 'in_payment' |
     * 'reversed'. Null/undefined for manually-uploaded documents.
     */
    paymentStatus?: string;
    /** ISO timestamp when paymentStatus was last read from Odoo. */
    paymentStatusUpdatedAt?: string;
}

export interface ProjectDate {
    id: number;
    type: ProjectDateType;
    customLabel?: string;
    date: Date;
    durationType?: DurationType;
    durationValue?: number;
    comment?: string;
    project: RenovationProject;
    isDeprecated: boolean;
    createdAt: Date;
}

export enum SuggestionType {
    PERMIT = "permit",
    RISK = "risk",
    PREPARATION = "preparation",
    PLANNING = "planning",
    SUPPLIER = "supplier"
}

export enum SuggestionStatus {
    SUGGESTED = "suggested",
    ACCEPTED = "accepted",
    REJECTED = "rejected",
    COMPLETED = "completed"
}

export interface RenovationBudget {
    id: number;
    title: string;
    reason?: string;
    createdAt: Date;
    buildingId: string;
    projects?: RenovationProject[];
    invoices?: RenovationInvoice[];
    budgetLines: RenovationBudgetLine[];
}

export interface RenovationProject {
    id: number;
    title: string;
    description?: string;
    status: ProjectStatus;
    /**
     * Rough top-down budget estimate captured early on the project. Excl.
     * VAT. `null` when not yet set.
     */
    roughBudgetEstimate?: number | null;
    buildingId: string;
    building: BuildingDTO;
    units: UnitDTO[];
    unitIds: string[];
    responsibleUserId: string;
    budgetIds: number[];
    createdAt: Date;
    updatedAt?: Date;
    /** @deprecated Use investmentAssignments on parts instead */
    budgetAssignments?: ProjectBudgetAssignment[];
    parts: ProjectPart[];
    dates?: ProjectDate[];
    sharePointFolderId?: string;
    sharePointFolderUrl?: string;
    budgetFolderId?: string;
    budgetFolderUrl?: string;
    offerFolderId?: string;
    offerFolderUrl?: string;
    ordersFolderId?: string;
    ordersFolderUrl?: string;
    invoicesFolderId?: string;
    invoicesFolderUrl?: string;
    preliminaryPlansFolderId?: string;
    preliminaryPlansFolderUrl?: string;
    studiesFolderId?: string;
    studiesFolderUrl?: string;
    permitsFolderId?: string;
    permitsFolderUrl?: string;
}

export enum OfferStatus {
    REQUESTED = "Aangevraagd",
    RECEIVED = "Ontvangen",
    CHOSEN = "Gekozen",
    DECLINED = "Afgewezen"
}

export interface RenovationInvoice {
    id: number;
    odooMoveLineId: number;
    amount: number;
    budgetId: number;
    offerId?: number;
    createdAt: Date;
    invoiceDate: Date;
}

export interface AnalyticDistribution {
    id: number;
    unit: UnitDTO;
    amount: number;
    budgetLine: RenovationBudgetLine;
    documentLine: DocumentLine;
    projectPart: ProjectPart;
    percentage: number;
    remarks?: string;
    /**
     * Waar deze kostenplaats vandaan komt:
     *  - "odoo"    overgenomen uit de kostenplaatsboeking in de boekhouding —
     *              die is de bron van waarheid en overschrijft de afleiding.
     *  - "project" afgeleid uit de voorziene kostenplaatsen van het
     *              projectonderdeel (of van het project).
     *  - NULL      handmatig ingegeven bij een offerte of op een budgetlijn.
     *
     * Zie yallyhub-be/src/renovation/kostenplaats/.
     */
    source?: "odoo" | "project" | null;
}

export enum ProjectPartSupplierStatus {
    REQUEST_PROPOSAL_NOT_SENT = 'aanvraag niet verzonden',
    REQUESTED_PROPOSAL = 'aanvraag verzonden',
    OFFER_RECEIVED = 'offerte ontvangen',
    OFFER_APPROVED = 'offerte goedgekeurd',
    OFFER_REJECTED = 'offerte afgekeurd',
}
export interface RenovationBudgetLine {
    id: number;
    description: string;
    amountExclVat: number;
    amountInclVat: number;
    analyticDistributions: AnalyticDistribution[];
    projectPartAssignments?: ProjectBudgetAssignment[];
    projectAssignments?: ProjectBudgetAssignment[];
    label: RenovationLabel;
    createdAt: Date;
}

export enum RenovationLabelType {
    ESTHETIC = "esthetisch",
    GENERAL = "algemeen",
    ENERGETIC = "energetisch",
    SAFETY = "veiligheid"
}

export interface RenovationLabel {
    id: number;
    name: string;
    type: RenovationLabelType;
    usageCount: number;
}

export interface ProjectBudgetAssignment {
    id: number;
    budgetLine: RenovationBudgetLine;
    assignedAmount: number; // incl VAT
    createdAt: Date;
    part: ProjectPart;
}

export interface BudgetAssignmentData {
    budgetId: number;
    assignedAmount: number;
    availableAmount: number;
    currentPhaseAmount: number;
}

export interface ProjectDependency {
    id: number;
    dependentPart: ProjectPart;
    dependsOnPart: ProjectPart;
    createdAt: Date;
}

export enum PartStatus {
    NOT_STARTED = "Niet gestart",
    REQUESTING_OFFERS = "Offertes aanvragen", 
    REVIEWING_OFFERS = "Offertes beoordelen",
    SUPPLIER_SELECTED = "Leverancier gekozen",
    IN_PROGRESS = "In uitvoering",
    COMPLETED = "Afgerond",
    ON_HOLD = "On hold"
}

export enum PartType {
    ELECTRICITY = "Elektriciteit",
    PLUMBING = "Sanitair",
    HEATING = "Verwarming",
    VENTILATION = "Ventilatie",
    WALLS = "Muren",
    FLOORS = "Vloeren",
    WINDOWS = "Ramen",
    DOORS = "Deuren",
    KITCHEN = "Keuken",
    BATHROOM = "Badkamer",
    ROOF = "Dak",
    OTHER = "Andere"
}

export interface ProjectPartSupplier {
    id: number;
    part: ProjectPart;
    supplier: ContactCompany;
    supplierFolderId?: string;
    supplierFolderUrl?: string;
    status?: ProjectPartSupplierStatus;
    createdAt: Date;
}

/** Unit distribution for an investment assignment */
export interface InvestmentAssignmentUnitDistribution {
    id: number;
    unit: {
        id: string;
        name?: string;
        netSurface?: number;
    };
    percentage: number;
    amount: number;
}

/** Investment assignment from part perspective */
export interface PartInvestmentAssignment {
    id: number;
    investmentItem: {
        id: number;
        title: string;
        estimatedCostExclVat?: number;
        budgetLabel?: 'STRATEGISCH' | 'ONVOORZIEN' | null;
    };
    assignedAmount: number;
    notes?: string;
    unitDistributions?: InvestmentAssignmentUnitDistribution[];
    createdAt: Date;
}

export interface ProjectPart {
    id: number;
    title: string;
    description?: string;
    status: PartStatus;
    label?: RenovationLabel;
    orderIndex: number;
    project: RenovationProject;
    documentLines: DocumentLine[];
    supplierAssignments: ProjectPartSupplier[];
    /** @deprecated Use investmentAssignments instead */
    budgetAssignments: ProjectBudgetAssignment[];
    /** Investment line assignments - source of budget */
    investmentAssignments?: PartInvestmentAssignment[];
    /** Voorziene kostenplaatsen — units the part will impact, picked
     *  provisionally before any budget is assigned. */
    provisionalUnits?: ProjectPartProvisionalUnit[];
    plannedStartDate?: Date;
    plannedEndDate?: Date;
    actualStartDate?: Date;
    actualEndDate?: Date;
    dependencies: ProjectDependency[];
    dependents: ProjectDependency[];
    emailCount?: number;
    createdAt: Date;
    updatedAt: Date;
}

/** Voorziene kostenplaats link — provisional unit on a project part. */
export interface ProjectPartProvisionalUnit {
    id: number;
    unit: { id: string; estimatedRentalValue?: number };
    mustBeEmpty: boolean;
    createdAt: Date;
}

/** Voorziene kostenplaats link — provisional unit on the project itself. */
export interface ProjectProvisionalUnit {
    id: number;
    unit: { id: string; estimatedRentalValue?: number };
    mustBeEmpty: boolean;
    createdAt: Date;
}

export interface FileLabel {
    id: number;
    label: string;
    resourceType?: string;
    documentType?: string;
    isRenovationLabel: boolean;
    requiresFinancialDetails: boolean;
    financialDocumentType?: FinancialDocumentType;
    folders?: any[];
}

export enum ProjectTaskStatus {
    TODO = "Te doen",
    IN_PROGRESS = "In behandeling",
    COMPLETED = "Afgerond"
}

export enum ProjectPhase {
    DESIGN = "Ontwerp fase",
    PLANNED = "Gepland",
    IN_PROGRESS = "In uitvoering",
    COMPLETED = "Afgerond",
    PAID = "Volledig gefactureerd"
}

export type BlockedType = 'internal' | 'external';

// ─── Documents Stats API types (§1.3 of spec) ────────────────────────────────

export interface StatsBucket {
  count: number;
  amount: number;
}

export interface StatsTypeBlock {
  goedgekeurd: StatsBucket;
  afgewezen: StatsBucket;
  ontvangen: StatsBucket;
  concept: StatsBucket;
  total: StatsBucket;
}

export interface StatsBlock {
  documentCount: number;
  totalAmountInclVat: number;
  byType: {
    offerte: StatsTypeBlock;
    factuur: StatsTypeBlock;
    bestelbon: StatsTypeBlock;
    contract: StatsTypeBlock;
    overig: StatsTypeBlock;
  };
}

export interface DocumentsStatsResponse {
  total: StatsBlock;
  filtered: StatsBlock;
}

export interface ProjectTask {
    id: number;
    title: string;
    description?: string;
    status: ProjectTaskStatus;
    assignedTo?: string;
    projectId: number;
    projectPartId?: number;
    orderIndex: number;
    projectPhase?: ProjectPhase;
    todos?: TodoItem[];
    isBlocked?: boolean;
    blockedType?: BlockedType;
    blockedReason?: string;
    blockedBy?: string;
    blockedAt?: Date;
    blockedAssignedTo?: string;
    createdAt: Date;
    updatedAt: Date;
}
 